import { useEffect, useState, type FormEvent } from "react";

import { apiClient, type CurrentAccountProfile } from "../api/client";
import { useAuth } from "../app/AuthContext";
import { PasswordInput } from "../components/PasswordInput";
import { errorMessage } from "./admin/errors";

export function MyAccountPage() {
  const { changePassword, logout, recoverMfa, session } = useAuth();
  const [profile, setProfile] = useState<CurrentAccountProfile | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient
      .getCurrentAccount()
      .then(setProfile)
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }, []);

  async function perform(action: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      return true;
    } catch (caught) {
      setError(errorMessage(caught));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const privileged = session?.role === "manager" || session?.role === "owner";
  return (
    <div className="page-stack account-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">个人资料与凭据</span>
          <h1>我的账户与安全</h1>
          <p>这里的操作只作用于当前账户，不会进入用户管理或修改其他成员。</p>
        </div>
        <span className="phase-badge">{session?.role ?? "user"}</span>
      </header>

      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="status-banner" role="status">
          {message}
        </p>
      )}

      <section className="flow-panel account-profile-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">当前身份</span>
            <h2>账户资料</h2>
          </div>
          <span className="phase-badge">{accountStateLabel(profile?.account_state)}</span>
        </div>
        {profile ? <ProfileFacts profile={profile} /> : <p>正在读取账户资料…</p>}
      </section>

      <div className="account-security-grid">
        <PasswordChangePanel
          busy={busy}
          onSubmit={(payload) =>
            perform(async () => {
              await changePassword(payload);
              setMessage("密码已更新，其他登录会话已撤销，当前会话已安全轮换。");
            })
          }
          privileged={privileged}
        />
        {privileged && (
          <MfaRecoveryPanel
            busy={busy}
            mfaState={profile?.mfa_state}
            onSubmit={(payload) =>
              perform(async () => {
                await recoverMfa(payload);
                setMessage("旧 MFA 因子已失效，请立即重新注册验证器。");
              })
            }
          />
        )}
      </div>

      <section className="flow-panel account-session-panel">
        <div>
          <span className="eyebrow">当前浏览器</span>
          <h2>会话操作</h2>
          <p>修改密码会撤销其他会话；退出会立即撤销当前浏览器会话。</p>
        </div>
        <button disabled={busy} onClick={() => void perform(logout)} type="button">
          安全退出当前会话
        </button>
      </section>
    </div>
  );
}

function ProfileFacts({ profile }: { profile: CurrentAccountProfile }) {
  return (
    <dl className="account-facts">
      <div>
        <dt>用户名</dt>
        <dd>{profile.username}</dd>
      </div>
      <div>
        <dt>显示名称</dt>
        <dd>{profile.display_name}</dd>
      </div>
      <div>
        <dt>主部门</dt>
        <dd>
          {profile.primary_department
            ? `${profile.primary_department.name} · ${profile.primary_department.code}`
            : "未设置"}
        </dd>
      </div>
      <div>
        <dt>角色</dt>
        <dd>{profile.role}</dd>
      </div>
      <div>
        <dt>密码状态</dt>
        <dd>{profile.password_state === "active" ? "正式密码" : "临时密码"}</dd>
      </div>
      <div>
        <dt>MFA 状态</dt>
        <dd>{mfaStateLabel(profile.mfa_state)}</dd>
      </div>
    </dl>
  );
}

function PasswordChangePanel({
  busy,
  privileged,
  onSubmit,
}: {
  busy: boolean;
  privileged: boolean;
  onSubmit: (payload: {
    current_password: string;
    new_password: string;
    step_up_code?: string;
  }) => Promise<boolean>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [stepUpCode, setStepUpCode] = useState("");
  const [localError, setLocalError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setLocalError("两次输入的新密码不一致。");
      return;
    }
    setLocalError("");
    const succeeded = await onSubmit({
      current_password: currentPassword,
      new_password: newPassword,
      ...(privileged ? { step_up_code: stepUpCode } : {}),
    });
    if (succeeded) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      setStepUpCode("");
    }
  }

  return (
    <form className="flow-panel account-security-form" onSubmit={(event) => void submit(event)}>
      <div>
        <span className="eyebrow">凭据轮换</span>
        <h2>修改密码</h2>
        <p>需要当前密码；特权账户还需当前 TOTP 或未使用的恢复码。</p>
      </div>
      {localError && (
        <p className="alert error" role="alert">
          {localError}
        </p>
      )}
      <label>
        当前密码
        <PasswordInput
          autoComplete="current-password"
          disabled={busy}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
          value={currentPassword}
        />
      </label>
      <label>
        新密码
        <PasswordInput
          autoComplete="new-password"
          disabled={busy}
          onChange={(event) => setNewPassword(event.target.value)}
          required
          value={newPassword}
        />
      </label>
      <label>
        确认新密码
        <PasswordInput
          autoComplete="new-password"
          disabled={busy}
          onChange={(event) => setConfirmation(event.target.value)}
          required
          value={confirmation}
        />
      </label>
      {privileged && (
        <label>
          TOTP 或恢复码
          <input
            autoComplete="one-time-code"
            disabled={busy}
            onChange={(event) => setStepUpCode(event.target.value)}
            required
            value={stepUpCode}
          />
        </label>
      )}
      <button disabled={busy} type="submit">
        更新并轮换会话
      </button>
    </form>
  );
}

function MfaRecoveryPanel({
  busy,
  mfaState,
  onSubmit,
}: {
  busy: boolean;
  mfaState?: CurrentAccountProfile["mfa_state"];
  onSubmit: (payload: { current_password: string; recovery_code: string }) => Promise<boolean>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    const succeeded = await onSubmit({
      current_password: currentPassword,
      recovery_code: recoveryCode,
    });
    if (succeeded) {
      setCurrentPassword("");
      setRecoveryCode("");
    }
  }

  return (
    <form className="flow-panel account-security-form" onSubmit={(event) => void submit(event)}>
      <div>
        <span className="eyebrow">验证器遗失</span>
        <h2>MFA 恢复</h2>
        <p>状态：{mfaStateLabel(mfaState)}。恢复会废弃旧因子和全部旧恢复码。</p>
      </div>
      <label>
        当前密码
        <PasswordInput
          autoComplete="current-password"
          disabled={busy}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
          value={currentPassword}
        />
      </label>
      <label>
        未使用的恢复码
        <input
          autoComplete="one-time-code"
          disabled={busy}
          onChange={(event) => setRecoveryCode(event.target.value)}
          required
          value={recoveryCode}
        />
      </label>
      <button disabled={busy || mfaState !== "enabled"} type="submit">
        废弃旧因子并重新注册
      </button>
    </form>
  );
}

function accountStateLabel(state?: CurrentAccountProfile["account_state"]): string {
  return { active: "已启用", disabled: "已停用", archived: "已归档" }[state ?? "active"];
}

function mfaStateLabel(state?: CurrentAccountProfile["mfa_state"]): string {
  return {
    not_required: "当前角色不要求",
    enrollment_required: "需要注册",
    enabled: "已启用",
  }[state ?? "not_required"];
}
